<script setup>
/**
 * Member Highlights on a restaurant's page — what members chose to say about
 * this place, in their own approved words.
 *
 * ONE list, two sources behind it (Kapil, 2026-09-19):
 *  - a Public Member Highlight the member approved after the restaurant asked,
 *    which anyone signed in sees; and
 *  - an active network share from one of YOUR mutual connections, which only
 *    you see.
 * The viewer doesn't need that distinction — it's a member saying something
 * about this restaurant, always attributed. So they interleave by date in one
 * list. The SERVER still keeps them apart and decides who may see which; this
 * only renders what comes back.
 *
 * No heading of its own: the tab above it already says Member Highlights
 * (Kapil, 2026-09-22).
 *
 * Signed-in members only (the endpoint is JWT + member-gated), matching how
 * the fans list works.
 *
 * Private notes never appear here. Nothing on this page is derived from a
 * member's Dining Profile — only from a share they explicitly approved.
 *
 * Each highlight says when, the way My Notes does — "1d ago" (Kapil,
 * 2026-09-25). The restaurant's own team always gets the tab, and when there
 * is nothing on it they are told how highlights get there (`showEmpty`).
 */
import { computed, onMounted, ref, watch } from 'vue'
import axios from '@axios'
import { mediaUrl } from '@core/utils/mediaUrl'
import { highlightAgo } from '@/utils/restaurantHighlights'

const props = defineProps({
  restaurantId: { type: String, default: '' },

  // Bumped by the page when the member shares or makes something private on
  // My Notes, so their own highlight appears here without a reload.
  refreshKey: { type: Number, default: 0 },

  // The restaurant's own team: an empty tab says how highlights get here.
  showEmpty: { type: Boolean, default: false },
})

// The parent shows the Member Highlights tab only when there is something in
// it for this viewer, and only the server knows that — so it is reported after
// the authorized fetch rather than guessed at.
const emit = defineEmits(['loaded'])

const highlights = ref({ public: [], network: [] })
const loading = ref(false)

// Newest first, both sources together. `at` can be missing, and those sort last
// rather than being dropped.
const rows = computed(() => [...highlights.value.public, ...highlights.value.network]
  .sort((a, b) => (b.at ? new Date(b.at).getTime() : 0) - (a.at ? new Date(a.at).getTime() : 0)))

async function load() {
  if (!props.restaurantId) return
  loading.value = true
  try {
    const { data } = await axios.get(`/profile/restaurant/${props.restaurantId}/highlights`)
    highlights.value = { public: data?.public || [], network: data?.network || [] }
    emit('loaded', rows.value.length > 0)
  } catch {
    highlights.value = { public: [], network: [] }
    emit('loaded', false)
  } finally {
    loading.value = false
  }
}

/**
 * No photo (or one that fails to load) falls back to their initials, the same
 * as the Fan List — first and last, never an empty circle (Kapil, 2026-09-22).
 * Attribution arrives already shortened ("Max G."), so the initials come from
 * the two parts of that name.
 */
function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  const first = parts[0][0] || ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] || '') : ''

  return (first + last).toUpperCase()
}

const failedImages = ref(new Set())

function onImageError(id) {
  failedImages.value = new Set([...failedImages.value, id])
}

onMounted(load)
watch(() => [props.restaurantId, props.refreshKey], load)
</script>

<template>
  <!--
    Kapil's words for the restaurant's team (2026-09-25): nothing is posted
    until the restaurant says so.
  -->
  <div
    v-if="!rows.length && showEmpty && !loading"
    class="member-highlights member-highlights--empty"
  >
    <p class="member-highlights__empty-title">
      No publicly displayed highlights yet from members
    </p>
    <p class="member-highlights__empty-sub">
      Restaurants get to approve public highlights before they are posted
    </p>
  </div>
  <div
    v-else-if="rows.length"
    class="member-highlights"
  >
    <ul class="member-highlights__list">
      <li
        v-for="h in rows"
        :key="h.shareId"
        class="member-highlights__row"
      >
        <!--
          The photo IS the attribution (Kapil, 2026-09-22): a member knows who
          their friends are, so no name or time under the words — tap the photo
          for their profile. The name stays for screen readers and on hover.
        -->
        <component
          :is="h.profileId ? 'RouterLink' : 'span'"
          :to="h.profileId ? `/m/${h.profileId}` : undefined"
          :aria-label="h.name"
          :title="h.name"
          class="member-highlights__who"
        >
          <VAvatar
            size="32"
            class="member-highlights__avatar"
          >
            <VImg
              v-if="h.profileImageId && !failedImages.has(h.profileImageId)"
              :src="mediaUrl(h.profileImageId)"
              cover
              @error="onImageError(h.profileImageId)"
            />
            <span v-else>{{ initials(h.name) }}</span>
          </VAvatar>
        </component>
        <div class="member-highlights__body">
          <p class="member-highlights__text">
            {{ h.text }}
          </p>
          <p
            v-if="highlightAgo(h.at)"
            class="member-highlights__time"
          >
            {{ highlightAgo(h.at) }}
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.member-highlights__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.member-highlights__row {
  display: flex;
  gap: 10px;
  padding-block: 8px;
}

.member-highlights__row + .member-highlights__row {
  border-block-start: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.member-highlights__who {
  flex: 0 0 auto;
  display: inline-flex;
  border-radius: 50%;
}

/* The Fan List's initials avatar, so a member reads the same everywhere. */
.member-highlights__avatar {
  background: var(--ds-brand-primary-light, #e9f4f3);
  color: var(--ds-brand-primary-darker, #0FA091);
  font-size: 13px;
  font-weight: 600;
}

/* Their line breaks are part of what they wrote. */
.member-highlights__body {
  min-width: 0;
}

.member-highlights__text {
  font-size: 14px;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

/* When — the time line My Notes uses. */
.member-highlights__time {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6));
}

.member-highlights--empty {
  padding-block: 8px;
}

.member-highlights__empty-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-body, rgb(var(--v-theme-on-surface)));
}

.member-highlights__empty-sub {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--ds-text-secondary, rgba(var(--v-theme-on-surface), 0.6));
}
</style>
